import type { en } from './en';

/**
 * Tiếng Việt. Values are copied **verbatim** from Android's
 * `values-vi/strings.xml` — not re-translated. The two apps are used side by
 * side by the same operators, and a fresh translation of "Bắt đầu kiểm tra"
 * would only introduce a second way of saying the identical thing.
 *
 * That includes the loanwords this product actually uses in the field — "link",
 * "die", "Device Key", "server", "app". They are not formalised here; an
 * operator who has been told a link is "die" needs the screen to say "die".
 *
 * `satisfies Record<keyof typeof en, string>` is the whole safety net: a key
 * added to `en.ts` and forgotten here fails the build.
 */
export const vi = {
  app_name: 'CheckLinks',

  nav_dashboard: 'Tổng quan',
  nav_results: 'Kết quả',
  nav_settings: 'Cài đặt',

  common_see_all: 'Xem tất cả',
  common_cancel: 'Huỷ',
  value_none: '—',
  value_unknown: 'Không rõ',
  unit_ms: '{0} ms',

  net_offline: 'Ngoại tuyến',
  net_no_connection_body: 'Không có kết nối mạng — thiết bị không thể kiểm tra link.',

  home_progress_label: 'Tiến trình',
  home_percent: '{0}%',
  home_checked_of_total: '{0} / {1}',
  home_waiting_network: 'Không có mạng — đang chờ. Sẽ tự chạy tiếp khi có mạng.',
  home_round: 'Vòng {0}/{1} — {2} link chưa đạt',
  home_next_round: 'Vòng {0} bắt đầu sau {1}',
  home_recent_label: 'Gần đây',
  home_start: 'Bắt đầu kiểm tra',
  home_run_again: 'Chạy lại',
  home_stop: 'Dừng kiểm tra',
  home_view_results: 'Xem kết quả',
  stat_alive: 'Sống',
  stat_dead: 'Die',
  stat_response: 'Phản hồi',

  err_generic_title: 'Có lỗi xảy ra',
  err_try_again_later: 'Thử lại sau.',
  err_key_invalid: 'Device Key không hợp lệ hoặc đã bị khoá.',
  err_key_required: 'Vui lòng nhập Device Key.',
  err_server_unreachable: 'Không kết nối được máy chủ. Kiểm tra mạng.',

  a11y_change_language: 'Đổi ngôn ngữ',
  a11y_copy: 'Sao chép',

  settings_language: 'Ngôn ngữ',
  settings_section_language: 'Ngôn ngữ',
  settings_measurement: 'Cách đo',
  settings_measurement_value: '{0} vòng, cách nhau {1} giây — do server đặt',
  settings_measurement_unknown: 'Chưa nhận được từ server',
  settings_section_device: 'Thiết bị',
  device_model: 'Thiết bị',
  device_app_version: 'Phiên bản app',
  device_network: 'Mạng hiện tại',
  device_carrier: 'Nhà mạng',
  device_region: 'Khu vực',
  device_ip: 'Địa chỉ IP',
  device_summary: '{0} · v{1}',

  filter_all: 'Tất cả {0}',
  filter_alive: 'Sống {0}',
  filter_dead: 'Die {0}',
  result_empty: 'Không có link nào',
  result_copied: 'Đã sao chép',

  keyentry_subtitle: 'Nhập Device Key được cấp để bắt đầu',
  keyentry_field_label: 'Device Key (dvk_…)',
  keyentry_submit: 'Kiểm tra và lưu',

  settings_section_device_key: 'Device key',
  device_key_remove: 'Gỡ device key khỏi máy này',
  device_key_remove_title: 'Gỡ device key của máy này?',
  device_key_remove_body:
    'Key đang lưu trên máy sẽ bị xoá. Lịch kiểm tra dừng lại và tự động chạy bị tắt. Bạn cần có lại key để cài đặt lại máy.',
  device_key_remove_action: 'Gỡ key',
  device_key_removing: 'Đang gỡ key…',
  device_key_removing_body: 'Đang dừng các lượt kiểm tra còn chạy.',

  // --- WebClip-only, translated here (no Android source to copy from) ---------

  ios_net_online: 'Đã kết nối',

  ios_last_check_label: 'Lần kiểm tra gần nhất',
  ios_last_check_never: 'Chưa gửi báo cáo nào',
  ios_last_check_ago: 'Đã gửi {0}',
  ios_last_check_at: 'lúc {0}',
  ios_last_check_stale:
    'Máy này đã im lặng hơn {0} phút. Kiểm tra: màn hình có tắt không, app có bị vuốt tắt không, máy có vừa khởi động lại không?',
  ios_next_run: 'Lượt tiếp theo sau {0}',
  ios_next_run_none: 'Chưa hẹn lượt nào',
  ios_running_now: 'Đang chạy một lượt kiểm tra…',

  ios_ago_just_now: 'vừa xong',
  ios_ago_seconds: '{0} giây trước',
  ios_ago_minutes: '{0} phút trước',
  ios_ago_hours: '{0} giờ {1} phút trước',
  ios_ago_days: '{0} ngày {1} giờ trước',

  ios_batch_saved: 'Lượt gần nhất: đã lưu {0} dòng',
  ios_batch_saved_skipped: 'Lượt gần nhất: đã lưu {0} dòng, bỏ qua {1}',
  ios_batch_dropped: '{0} link chưa chạy đủ số vòng — cố ý không gửi',

  ios_phase_fetching: 'Đang lấy cấu hình và danh sách link…',
  ios_phase_submitting: 'Đang gửi kết quả…',

  // Dấu hỏi thuộc về chính từ này, không phải ký tự nối thêm trong code — xem
  // ghi chú ở en.ts (SPEC §13).
  ios_status_alive_maybe: 'Sống?',

  ios_status_unmeasurable: 'không đo được',
  ios_error_mixed_content:
    'domain http:// thường — Safari chặn nội dung hỗn hợp. Đây không phải kết luận về link.',

  ios_key_revoked_title: 'Device Key bị từ chối',
  ios_key_revoked_body:
    'Server trả về 401 — key này đã hết hạn hoặc máy đã bị khoá. App đã dừng hẳn và sẽ không thử lại.',
  ios_key_revoked_hint:
    'Key nằm trong URL của icon nên không thể đổi từ trong app. Hãy xin quản trị viên link cài đặt mới, xoá icon cũ rồi Thêm vào Màn hình chính lại.',

  ios_settings_section_operation: 'Vận hành',
  ios_settings_schedule: 'Lịch chạy',
  ios_settings_schedule_value: 'Mỗi {0} phút — do server đặt',
  ios_settings_schedule_default: 'Mỗi {0} phút — mặc định, không đọc được cấu hình từ server',
  ios_settings_autorun_off:
    'Server đang tắt tự động kiểm tra. Máy vẫn thức dậy và hỏi lại, để có thể bật lại từ xa.',
  ios_settings_foreground_only:
    'App chỉ chạy khi màn hình đang bật và WebClip đang ở phía trước. App không tự mở lại sau khi khởi động lại máy. Đây là giới hạn của nền tảng, không phải lỗi.',

  ios_settings_section_device_setup: 'Thiết lập bắt buộc trên máy',
  ios_setup_autolock: 'Cài đặt → Màn hình và Độ sáng → Tự động khoá: Không bao giờ',
  ios_setup_lowpower: 'Tắt Chế độ nguồn điện thấp',
  ios_setup_charger: 'Luôn cắm sạc',
  ios_setup_guided_access: 'Bật Truy cập được hướng dẫn, khoá vào WebClip này',
  ios_setup_note:
    'App không thể kiểm tra hay thay đổi những mục này — Safari không có quyền truy cập.',

  ios_device_platform: 'Nền tảng',
  ios_device_user_agent: 'User agent',

  ios_caveat:
    '“Sống” ở đây nghĩa là “có thể còn sống”, không phải “đã xác minh là sống”. App này chỉ đo được một địa chỉ có phản hồi hay không; nó không đọc được mã trạng thái và không phân biệt được trang thật với trang chặn của nhà mạng. Đừng gộp các con số này với số liệu của app Android ở cùng một mức tin cậy.',

  ios_copy_failed: 'Không sao chép được',
} satisfies Record<keyof typeof en, string>;
